import { use } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ModeEditOutlineOutlined from '@mui/icons-material/ModeEditOutlineOutlined';
import { Typography } from '@mui/material';
import Button from '@mui/material/Button';
import OfficeBuildingMarker from 'shared/components/icons/office-building-marker';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';

import FeatureSubscriptionRow from 'features/feature-subscriptions/components/feature-subscription-row/feature-subscription-row';
import LocationFeatureSubscriptionsProviderContext from 'features/feature-subscriptions/contexts/location-feature-subscriptions-provider-context';
import useFeatureSubscriptionFeatureFlag from 'features/feature-subscriptions/hooks/feature-subscription-feature-flag';
import styles from './location-feature-subscriptions-details.module.scss';

const LocationFeatureSubscriptionsDetails = (): React.JSX.Element => {
  const { t } = useTranslation('featureSubscriptions');

  const { isFeatureFlagForSubscriptionEnabled } = useFeatureSubscriptionFeatureFlag();

  const {
    selectedLocation,
    activeEditor,
    detailsViewingMode,
    handleDetailsEdit,
    handleDetailsCancel,
    locationForm,
    submitLocationFeatureSubscriptions,
    updateLocationFeatureSubscriptionsIsPending,
    readLocationFeatureSubscriptionsIsFetching,
  } = use(LocationFeatureSubscriptionsProviderContext);

  const isViewing = detailsViewingMode === 'viewing';
  const isTenantEditor = activeEditor === 'tenant';

  const iconSx = {
    fontSize: '16px',
  };

  const renderViewingModeButtons = (): React.JSX.Element => {
    if (isTenantEditor) {
      return <></>;
    }

    return (
      <Button startIcon={<ModeEditOutlineOutlined />} variant="text" onClick={handleDetailsEdit} key="editButton">
        {t('organisationSubscriptions.editFormButton')}
      </Button>
    );
  };

  const renderEditingModeButtons = (): React.JSX.Element => (
    <>
      <Button variant="text" onClick={handleDetailsCancel} key="cancelButton">
        {t('organisationSubscriptions.cancelButton')}
      </Button>
      <Button startIcon={<CheckRounded />} variant="contained" type="submit" key="saveButton">
        {t('organisationSubscriptions.saveButton')}
      </Button>
    </>
  );

  const renderSubscriptions = (): React.JSX.Element => {
    if (!selectedLocation) {
      return <></>;
    }

    return (
      <div className={styles.subscriptions}>
        {selectedLocation.featureSubscriptions.map((subscription, index) => {
          const isDisabledByFeatureFlag = !isFeatureFlagForSubscriptionEnabled(subscription.featureSubscriptionType);

          return (
            <FeatureSubscriptionRow
              key={subscription.featureSubscriptionType}
              index={index}
              isActive={subscription.isActive}
              featureSubscriptionType={subscription.featureSubscriptionType}
              isViewing={isViewing}
              fieldNamePrefix="featureSubscriptions"
              disabled={isDisabledByFeatureFlag}
              tooltipText={!isViewing && isDisabledByFeatureFlag ? t('organisationSubscriptions.disabledByFeatureFlagTooltip') : undefined}
            />
          );
        })}
      </div>
    );
  };

  return (
    <FormProvider {...locationForm}>
      <form
        onSubmit={locationForm.handleSubmit(submitLocationFeatureSubscriptions)}
        className={`${styles.container} ${selectedLocation ? styles.isOpen : ''}`}
      >
        <div className={styles.header}>
          {selectedLocation && <div className={styles.actions}>{isViewing ? renderViewingModeButtons() : renderEditingModeButtons()}</div>}
          <div className={styles.title}>
            <Typography variant="h3">{t('locationSubscriptionsDetails.title')}</Typography>
            <div className={styles.location}>
              <OfficeBuildingMarker sx={iconSx} />
              <Typography variant="body1">{selectedLocation?.locationName}</Typography>
            </div>
          </div>
        </div>
        <div className={styles.content}>
          {updateLocationFeatureSubscriptionsIsPending || readLocationFeatureSubscriptionsIsFetching ? (
            <LoadingIndicator />
          ) : (
            renderSubscriptions()
          )}
        </div>
      </form>
    </FormProvider>
  );
};

export default LocationFeatureSubscriptionsDetails;
